import React from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import AddIcon from 'components/material-symbol/icons/Add'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { useAuth } from 'lib/auth'
import { useCoachCampaigns } from './api-hooks'
import { StatusBadge } from './badges'
import { formatCampaignDate, pluralize } from './format'
import useStyles from './styles'
import SectionTitle from 'components/section-title'

const campaignPath = (basePath, campaign) =>
  campaign.status === 'DRAFT'
    ? `${basePath}/${campaign.id}/edit`
    : `${basePath}/${campaign.id}`

export function CampaignListView({ campaigns, basePath }) {
  const classes = useStyles()
  const newCampaignButton = (
    <Button
      variant="contained"
      color="primary"
      className={classes.pillButton}
      component={RouterLink}
      to={`${basePath}/new`}
      startIcon={<AddIcon />}
    >
      New Campaign
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Campaigns"
        actions={newCampaignButton}
        fixedMobileActions
        keepActionsInline
      />
      <Box className={classes.pageContent}>
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <SectionTitle>Your campaigns</SectionTitle>
            <Typography className={classes.cardSubtitle}>
              Send one message to a group of students. Each student gets it in
              their chat with you.
            </Typography>
          </Box>
          {campaigns.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyTitle}>
                No campaigns yet
              </Typography>
              <Typography className={classes.emptyCopy}>
                Tell students about open lesson times, clinics, or programs, and
                see who books.
              </Typography>
              {newCampaignButton}
            </Box>
          ) : (
            <Box>
              {campaigns.map(campaign => (
                <Box
                  key={campaign.id}
                  component={RouterLink}
                  to={campaignPath(basePath, campaign)}
                  className={classes.row}
                  data-testid={`campaign-${campaign.id}`}
                >
                  <Box className={classes.rowBody}>
                    <Typography className={classes.rowTitle}>
                      {campaign.name}
                    </Typography>
                    <Typography className={classes.rowDetail}>
                      {formatCampaignDate(campaign)}
                      {campaign.recipientCount > 0
                        ? ` · ${pluralize(campaign.recipientCount, 'student')}`
                        : ''}
                    </Typography>
                  </Box>
                  {campaign.status === 'SENT' ? (
                    <Box className={classes.rowStats}>
                      <Box className={classes.rowStat}>
                        <Typography className={classes.rowStatValue}>
                          {campaign.results.directBookingCount}
                        </Typography>
                        <Typography className={classes.rowStatLabel}>
                          Booked from link
                        </Typography>
                      </Box>
                      <Box className={classes.rowStat}>
                        <Typography className={classes.rowStatValue}>
                          {campaign.results.influencedBookingCount}
                        </Typography>
                        <Typography className={classes.rowStatLabel}>
                          Booked later
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                  <StatusBadge status={campaign.status} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

CampaignListView.propTypes = {
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      recipientCount: PropTypes.number.isRequired,
      results: PropTypes.shape({
        directBookingCount: PropTypes.number.isRequired,
        influencedBookingCount: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  basePath: PropTypes.string.isRequired,
}

const CampaignList = () => {
  const { url } = useRouteMatch()
  const { user } = useAuth()
  const hasPaidTier = user?.coach?.hasPaidTierSubscription
  const { data, loading, error } = useCoachCampaigns({ skip: !hasPaidTier })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  return (
    <CampaignListView
      campaigns={data?.coachCampaigns || []}
      basePath={url.replace(/\/$/, '')}
    />
  )
}

export default CampaignList
